/**
 * Google Analytics 4 Event Tracking Utility
 * 
 * This utility provides custom event tracking for GA4
 * All events are sent to GA4 with proper categorization
 */

// Check if gtag is available
const isGtagAvailable = () => typeof window !== 'undefined' && typeof window.gtag === 'function';

/**
 * Track custom events in GA4
 * @param {string} eventName - Name of the event
 * @param {object} eventParams - Additional parameters for the event
 */
export const trackEvent = (eventName, eventParams = {}) => {
  if (isGtagAvailable()) {
    window.gtag('event', eventName, eventParams);
    console.log('GA4 Event:', eventName, eventParams);
  }
};

/**
 * Track form submissions
 */
export const trackFormSubmission = (formName, formData = {}) => {
  trackEvent('form_submission', {
    form_name: formName,
    ...formData
  });
};

/**
 * Track button clicks
 */
export const trackButtonClick = (buttonName, buttonLocation = '') => {
  trackEvent('button_click', {
    button_name: buttonName,
    button_location: buttonLocation
  });
};

/**
 * Track navigation clicks
 */
export const trackNavigation = (linkName, linkUrl) => {
  trackEvent('navigation_click', {
    link_name: linkName,
    link_url: linkUrl
  });
};

/**
 * Track video interactions
 */
export const trackVideoPlay = (videoTitle, videoUrl = '') => {
  trackEvent('video_play', {
    video_title: videoTitle,
    video_url: videoUrl
  });
};

export const trackVideoPause = (videoTitle, videoUrl = '') => {
  trackEvent('video_pause', {
    video_title: videoTitle,
    video_url: videoUrl
  });
};

export const trackVideoComplete = (videoTitle, videoUrl = '') => {
  trackEvent('video_complete', {
    video_title: videoTitle,
    video_url: videoUrl
  });
};

/**
 * Track user authentication
 */
export const trackLogin = (userRole = 'user') => {
  trackEvent('login', {
    method: 'email',
    user_role: userRole
  });
};

export const trackLogout = () => {
  trackEvent('logout', {});
};

export const trackSignup = (userRole = 'user') => {
  trackEvent('sign_up', {
    method: 'email',
    user_role: userRole
  });
};

/**
 * Track content engagement
 */
export const trackBlogView = (articleTitle, articleSlug, category = '') => {
  trackEvent('blog_article_view', {
    article_title: articleTitle,
    article_slug: articleSlug,
    article_category: category
  });
};

export const trackBlogSearch = (searchTerm) => {
  trackEvent('search', {
    search_term: searchTerm,
    search_location: 'blog'
  });
};

/**
 * Track CTA interactions
 */
export const trackCTAClick = (ctaName, ctaLocation, ctaDestination = '') => {
  trackEvent('cta_click', {
    cta_name: ctaName,
    cta_location: ctaLocation,
    cta_destination: ctaDestination
  });
};

/**
 * Track downloads
 */
export const trackDownload = (fileName, fileType = '') => {
  trackEvent('file_download', {
    file_name: fileName,
    file_type: fileType
  });
};

/**
 * Track external link clicks
 */
export const trackExternalLink = (linkUrl, linkText = '') => {
  trackEvent('external_link_click', {
    link_url: linkUrl,
    link_text: linkText
  });
};

/**
 * Track page views (useful for SPA navigation)
 */
export const trackPageView = (pagePath, pageTitle) => {
  if (isGtagAvailable()) {
    window.gtag('config', 'G-CTTS05B56W', {
      page_path: pagePath,
      page_title: pageTitle
    });
  }
};

/**
 * Track conversions
 */
export const trackConversion = (conversionType, value = 0) => {
  trackEvent('conversion', {
    conversion_type: conversionType,
    value: value
  });
};

/**
 * Track errors
 */
export const trackError = (errorMessage, errorLocation = '') => {
  trackEvent('error', {
    error_message: errorMessage,
    error_location: errorLocation
  });
};

/**
 * Marketplace Event Tracking
 */
export const trackMarketplaceSearch = (searchTerm, resultsCount) => {
  trackEvent('marketplace_search', {
    search_term: searchTerm,
    results_count: resultsCount,
    search_location: 'ecosystem_marketplace'
  });
};

export const trackMarketplaceFilter = (filterType, filterValue) => {
  trackEvent('marketplace_filter', {
    filter_type: filterType,
    filter_value: filterValue
  });
};

export const trackProductView = (productName, productId, vendorName) => {
  trackEvent('product_view', {
    product_name: productName,
    product_id: productId,
    vendor_name: vendorName,
    content_type: 'marketplace_product'
  });
};

export const trackVendorView = (vendorName, vendorId, productCount) => {
  trackEvent('vendor_view', {
    vendor_name: vendorName,
    vendor_id: vendorId,
    product_count: productCount,
    content_type: 'marketplace_vendor'
  });
};

export const trackMarketplaceContact = (contactType, vendorName, productName = '', region = '') => {
  trackEvent('marketplace_contact', {
    contact_type: contactType,
    vendor_name: vendorName,
    product_name: productName,
    region: region
  });
};

export const trackProductClick = (productName, sourceLocation) => {
  trackEvent('product_click', {
    product_name: productName,
    source_location: sourceLocation
  });
};

export const trackVendorClick = (vendorName, sourceLocation) => {
  trackEvent('vendor_click', {
    vendor_name: vendorName,
    source_location: sourceLocation
  });
};
